import { EDIT_NO, HIDE, SHOW } from '@query/shared';
import { HEIGHT } from '../constants.js';
import { useAppStore } from '../store.js';
import { HistoryDialog } from './HistoryDialog.js';
import { IntervalDialog } from './IntervalDialog.js';
import { NestedMenu } from './NestedMenu.js';
import { ShareButtons } from './ShareButtons.js';

/**
 * Панель инструментов — перенос navBar вместе с history()/intervalDateDialog()/modify()
 * (AppFC.kt:282-445).
 *
 * ⚠️ Разметка повторяет оригинал намеренно: **в `<li>` завёрнуто только меню**, остальные
 * иконки — прямые потомки `<ul class="topmenu">`. Это не небрежность, а требование CSS
 * (`css/menu.css`):
 *   - `.topmenu > li { border-left: 1px solid black; float: left }` — если завернуть каждую
 *     группу в `<li>`, между группами появляются лишние вертикальные разделители,
 *     которых в оригинале нет (у меню как у `:first-child` рамка снята правилом);
 *   - `float: left` на всех элементах схлопывает высоту `<ul>`, и следующая строка
 *     (обновление + название запроса) обтекает плавающие иконки и уезжает вправо.
 * Внутри `<ul>` прямые `<img>` — невалидный HTML, но ровно так сделано в fullWeb,
 * и переверстка сломала бы совпадение с оригиналом.
 */
export function Toolbar() {
  const showNav = useAppStore((s) => s.showNav);
  const showShare = useAppStore((s) => s.showShare);
  const menuItems = useAppStore((s) => s.menuItems);
  const queryHistory = useAppStore((s) => s.queryHistory);
  const historyCurrent = useAppStore((s) => s.historyCurrent);
  const newTab = useAppStore((s) => s.newTab);
  const historyDialogOpen = useAppStore((s) => s.historyDialogOpen);
  const intervalDialogOpen = useAppStore((s) => s.intervalDialogOpen);
  const dateFrom = useAppStore((s) => s.dateFrom);
  const dateTo = useAppStore((s) => s.dateTo);
  const titleCurrent = useAppStore((s) => s.titleCurrent);

  const goHistory = useAppStore((s) => s.goHistory);
  const openHistoryEntry = useAppStore((s) => s.openHistoryEntry);
  const toggleNewTab = useAppStore((s) => s.toggleNewTab);
  const setHistoryDialogOpen = useAppStore((s) => s.setHistoryDialogOpen);
  const setIntervalDialogOpen = useAppStore((s) => s.setIntervalDialogOpen);
  const setDateFrom = useAppStore((s) => s.setDateFrom);
  const setDateTo = useAppStore((s) => s.setDateTo);
  const toggleShowShare = useAppStore((s) => s.toggleShowShare);
  const shareUrl = useAppStore((s) => s.shareUrl);
  const editIcon = useAppStore((s) => s.editIcon);
  const showButton = useAppStore((s) => s.showButton);
  const toggleEdit = useAppStore((s) => s.toggleEdit);
  const deleteSelectedRows = useAppStore((s) => s.deleteSelectedRows);
  const copySelectedRows = useAppStore((s) => s.copySelectedRows);
  const saveChanges = useAppStore((s) => s.saveChanges);
  const refreshRow = useAppStore((s) => s.refreshRow);
  const setResultUpdate = useAppStore((s) => s.setResultUpdate);

  const canPrev = queryHistory.length > 1 && historyCurrent > 0;
  const canNext = historyCurrent < queryHistory.length - 1 && historyCurrent > -1;

  return (
    <>
      <nav className={showNav}>
        <ul className="topmenu">
          <li>{showNav === SHOW ? <NestedMenu data={menuItems()} /> : null}</li>

          {/* history() — AppFC.kt:282-315 */}
          <img
            src={`img/prev${canPrev ? '' : 'H'}.png`}
            title="prev query"
            height={HEIGHT}
            alt="prev query"
            onClick={() => void goHistory(-1)}
          />
          <img
            src="img/history.png"
            title="query history"
            height={HEIGHT}
            alt="query history"
            className={queryHistory.length > 0 ? SHOW : HIDE}
            onClick={() => setHistoryDialogOpen(true)}
          />
          <img
            src={`img/next${canNext ? '' : 'H'}.png`}
            title="next query"
            height={HEIGHT}
            alt="next query"
            onClick={() => void goHistory(1)}
          />

          {/* intervalDateDialog() — AppFC.kt:245-280 */}
          <img
            src="img/interval.png"
            title="Interval Date"
            height={HEIGHT}
            alt="Interval Date"
            onClick={() => setIntervalDialogOpen(true)}
          />

          {/* refresh() — AppFC.kt:104-117: перезагружает строки (refreshRow), а не меню */}
          <img
            src="img/refresh.png"
            title="refresh"
            height={HEIGHT}
            alt="refresh"
            onClick={() => {
              setResultUpdate('Trying to fetch... ');
              void refreshRow().then(() => setResultUpdate(''));
            }}
          />

          {/* modify() — AppFC.kt:317-404 */}
          <img
            src="img/help.png"
            title="help"
            height={HEIGHT}
            alt="help"
            onClick={() => window.open('help.html')}
          />
          <img
            className={SHOW}
            src="img/share.png"
            title="share"
            height={HEIGHT}
            alt="share"
            onClick={toggleShowShare}
          />
          <span className={showShare}>
            <ShareButtons url={shareUrl()} />
          </span>
          <img
            src={`img/${editIcon}.jpg`}
            title="edit"
            height={HEIGHT}
            alt="edit"
            className={showButton}
            onClick={toggleEdit}
          />
          <img
            src="img/delete.png"
            title="delete"
            height={HEIGHT}
            alt="delete"
            className={showButton}
            onClick={deleteSelectedRows}
          />
          <img
            src="img/copy.png"
            title="copy"
            height={HEIGHT}
            alt="copy"
            className={showButton}
            onClick={() => void copySelectedRows()}
          />
          <img
            src="img/save.png"
            title="save"
            height={HEIGHT}
            alt="save"
            className={editIcon === EDIT_NO ? HIDE : SHOW}
            onClick={() => void saveChanges()}
          />
        </ul>
      </nav>

      <HistoryDialog
        open={historyDialogOpen}
        entries={queryHistory}
        titleCurrent={titleCurrent}
        newTab={newTab}
        onToggleNewTab={toggleNewTab}
        onSelect={(index) => void openHistoryEntry(index)}
        onClose={() => setHistoryDialogOpen(false)}
      />

      <IntervalDialog
        open={intervalDialogOpen}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onChangeDateFrom={setDateFrom}
        onChangeDateTo={setDateTo}
        onClose={() => setIntervalDialogOpen(false)}
      />
    </>
  );
}
